/**
 * MixerPanel - Interface de la console de mixage
 * Utilise VerticalSlider pour des faders propres sans hack de rotation
 */

import { VerticalSlider } from '../components/VerticalSlider.js';

export class MixerPanel {
    constructor(audioEngine, store) {
        this.audioEngine = audioEngine;
        this.mixer = audioEngine.getMixer();
        this.store = store;
        this.panelId = 'mixer';

        // Configuration des canaux
        this.channelConfig = [
            { id: 'tb303_1', name: 'TB-303 #1', color: '#ff6b35' },
            { id: 'tb303_2', name: 'TB-303 #2', color: '#ff6b35' },
            { id: 'tr808', name: 'TR-808', color: '#4ecdc4' },
            { id: 'tr909', name: 'TR-909', color: '#ffe66d' },
            { id: 'minimoog', name: 'Minimoog', color: '#d4a574' },
            { id: 'ms20', name: 'MS-20', color: '#7b8fa1' },
            { id: 'jupiter8', name: 'Jupiter-8', color: '#e85d04' },
            { id: 'theremin', name: 'Theremin', color: '#9b59b6' },
            { id: 'arp2600', name: 'ARP 2600', color: '#3a7bd5' },
            { id: 'juno106', name: 'Juno-106', color: '#00bcd4' },
            { id: 'sh101', name: 'SH-101', color: '#8bc34a' },
            { id: 'minimod', name: 'MiniMod', color: '#ff00ff' }
        ];

        this.sliders = {};
        this.meters = {};
        this.animationId = null;

        // Performance: meters conditionnels et throttlés
        this.metersEnabled = this.mixer.metersEnabled;
        this.meterIntervalMs = this.audioEngine?.performance?.lowPower ? 1000 / 12 : 1000 / 30;
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();

        container.innerHTML = `
            <div class="machine mixer-panel ${isCollapsed ? 'collapsed' : ''}" id="mixer_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="mixer_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    MIXER <span class="brand">Console</span>
                </div>
                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="mixer_content">
                <div class="mixer-channels">
                    ${this.channelConfig.map(ch => this.renderChannelStructure(ch)).join('')}
                    <div class="mixer-channel master-channel">
                        <div class="channel-label">MASTER</div>
                        <div class="meter-fader-row">
                            <div class="meter-container" id="meter_master">
                                <div class="meter-bar" id="meterBar_master"></div>
                                <div class="meter-peak" id="meterPeak_master"></div>
                            </div>
                            <div class="master-fader-container" id="masterFaderContainer"></div>
                        </div>
                        <div class="channel-buttons"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();

        if (!isCollapsed) {
            this.createSliders();
            this.setupButtonListeners();
            if (this.metersEnabled) {
                this.initMeters();
                this.startMeterAnimation();
            }
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('mixer_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    renderChannelStructure(config) {
        const muted = this.mixer.isMuted(config.id);
        const soloed = this.mixer.isSoloed(config.id);

        return `
            <div class="mixer-channel" data-channel="${config.id}">
                <div class="channel-label" style="color: ${config.color}">${config.name}</div>
                <div class="sends-grid">
                    <div class="send-container" id="dlySend_${config.id}_container"><span class="send-title">DLY</span></div>
                    <div class="send-container" id="rvbSend_${config.id}_container"><span class="send-title">RVB</span></div>
                    <div class="send-container" id="chrSend_${config.id}_container"><span class="send-title">CHR</span></div>
                    <div class="send-container" id="phsSend_${config.id}_container"><span class="send-title">PHS</span></div>
                    <div class="send-container" id="flgSend_${config.id}_container"><span class="send-title">FLG</span></div>
                    <div class="send-container" id="crsSend_${config.id}_container"><span class="send-title">BIT</span></div>
                    <div class="send-container" id="trmSend_${config.id}_container"><span class="send-title">TRM</span></div>
                    <div class="send-container" id="dstSend_${config.id}_container"><span class="send-title">DST</span></div>
                </div>
                <div class="meter-fader-row">
                    <div class="meter-container" id="meter_${config.id}">
                        <div class="meter-bar" id="meterBar_${config.id}"></div>
                        <div class="meter-peak" id="meterPeak_${config.id}"></div>
                    </div>
                    <div class="fader-slot" id="fader_${config.id}_container"></div>
                </div>
                <div class="channel-buttons">
                    <button class="mixer-btn mute-btn ${muted ? 'active' : ''}" id="mute_${config.id}">M</button>
                    <button class="mixer-btn solo-btn ${soloed ? 'active' : ''}" id="solo_${config.id}">S</button>
                </div>
            </div>
        `;
    }

    createSliders() {
        // Créer les sliders pour chaque canal
        this.channelConfig.forEach(config => {
            const id = config.id;

            // Fader principal (hauteur synchronisée avec le meter CSS)
            const faderContainer = document.getElementById(`fader_${id}_container`);
            this.sliders[`fader_${id}`] = new VerticalSlider(faderContainer, {
                min: 0, max: 1, value: this.mixer.getVolume(id), step: 0.01,
                height: 120, variant: 'fader', showValue: true
            }).onChange(value => {
                this.mixer.setVolume(id, value);
                this.saveState();
            });

            // All sends (height réduit pour tenir dans la grille)
            const sendHeight = 35;
            const sends = [
                { key: 'dly', get: 'getDelaySend', set: 'setDelaySend' },
                { key: 'rvb', get: 'getReverbSend', set: 'setReverbSend' },
                { key: 'chr', get: 'getChorusSend', set: 'setChorusSend' },
                { key: 'phs', get: 'getPhaserSend', set: 'setPhaserSend' },
                { key: 'flg', get: 'getFlangerSend', set: 'setFlangerSend' },
                { key: 'crs', get: 'getCrusherSend', set: 'setCrusherSend' },
                { key: 'trm', get: 'getTremoloSend', set: 'setTremoloSend' },
                { key: 'dst', get: 'getDistortionSend', set: 'setDistortionSend' }
            ];

            sends.forEach(send => {
                const container = document.getElementById(`${send.key}Send_${id}_container`);
                this.sliders[`${send.key}_${id}`] = new VerticalSlider(container, {
                    min: 0, max: 1, value: this.mixer[send.get](id), step: 0.01,
                    height: sendHeight, variant: 'send', showValue: false
                }).onChange(value => {
                    this.mixer[send.set](id, value);
                    this.saveState();
                });
            });
        });

        // Master fader (même hauteur que les autres)
        const masterContainer = document.getElementById('masterFaderContainer');
        this.sliders.master = new VerticalSlider(masterContainer, {
            min: 0, max: 1, value: 1, step: 0.01,
            height: 120, variant: 'fader', showValue: true
        }).onChange(value => {
            const db = value === 0 ? -Infinity : 20 * Math.log10(value);
            this.audioEngine.setMasterVolume(db);
        });
    }

    setupButtonListeners() {
        this.channelConfig.forEach(config => {
            const muteBtn = document.getElementById(`mute_${config.id}`);
            const soloBtn = document.getElementById(`solo_${config.id}`);

            muteBtn.addEventListener('click', () => {
                const newState = !this.mixer.isMuted(config.id);
                this.mixer.setMute(config.id, newState);
                muteBtn.classList.toggle('active', newState);
                this.saveState();
            });

            soloBtn.addEventListener('click', () => {
                const newState = !this.mixer.isSoloed(config.id);
                this.mixer.setSolo(config.id, newState);
                this.updateSoloButtons();
            });
        });
    }

    updateSoloButtons() {
        this.channelConfig.forEach(config => {
            const soloBtn = document.getElementById(`solo_${config.id}`);
            const soloed = this.mixer.isSoloed(config.id);
            soloBtn.classList.toggle('active', soloed);
        });
    }

    saveState() {
        if (this.store) {
            const mixerState = {};
            this.channelConfig.forEach(config => {
                const id = config.id;
                mixerState[id] = {
                    volume: this.mixer.getVolume(id),
                    delaySend: this.mixer.getDelaySend(id),
                    reverbSend: this.mixer.getReverbSend(id),
                    chorusSend: this.mixer.getChorusSend(id),
                    phaserSend: this.mixer.getPhaserSend(id),
                    flangerSend: this.mixer.getFlangerSend(id),
                    crusherSend: this.mixer.getCrusherSend(id),
                    tremoloSend: this.mixer.getTremoloSend(id),
                    distortionSend: this.mixer.getDistortionSend(id),
                    muted: this.mixer.isMuted(id)
                };
            });
            this.store.setMixerState(mixerState);
        }
    }

    loadState(state) {
        if (!state) return;

        const sendMap = [
            { key: 'delaySend', slider: 'dly', setter: 'setDelaySend' },
            { key: 'reverbSend', slider: 'rvb', setter: 'setReverbSend' },
            { key: 'chorusSend', slider: 'chr', setter: 'setChorusSend' },
            { key: 'phaserSend', slider: 'phs', setter: 'setPhaserSend' },
            { key: 'flangerSend', slider: 'flg', setter: 'setFlangerSend' },
            { key: 'crusherSend', slider: 'crs', setter: 'setCrusherSend' },
            { key: 'tremoloSend', slider: 'trm', setter: 'setTremoloSend' },
            { key: 'distortionSend', slider: 'dst', setter: 'setDistortionSend' }
        ];

        this.channelConfig.forEach(config => {
            const id = config.id;
            const channelState = state[id];
            if (channelState) {
                if (channelState.volume !== undefined) {
                    this.mixer.setVolume(id, channelState.volume);
                    this.sliders[`fader_${id}`]?.setValue(channelState.volume);
                }

                sendMap.forEach(send => {
                    if (channelState[send.key] !== undefined) {
                        this.mixer[send.setter](id, channelState[send.key]);
                        this.sliders[`${send.slider}_${id}`]?.setValue(channelState[send.key]);
                    }
                });

                if (channelState.muted !== undefined) {
                    this.mixer.setMute(id, channelState.muted);
                    const muteBtn = document.getElementById(`mute_${id}`);
                    if (muteBtn) muteBtn.classList.toggle('active', channelState.muted);
                }
            }
        });
    }

    initMeters() {
        // Initialize meter references for each channel
        this.channelConfig.forEach(config => {
            this.meters[config.id] = {
                bar: document.getElementById(`meterBar_${config.id}`),
                peak: document.getElementById(`meterPeak_${config.id}`),
                peakValue: -Infinity,
                peakHoldTime: 0
            };
        });

        // Master meter
        this.meters.master = {
            bar: document.getElementById('meterBar_master'),
            peak: document.getElementById('meterPeak_master'),
            peakValue: -Infinity,
            peakHoldTime: 0
        };
    }

    startMeterAnimation() {
        let lastUpdate = 0;
        const updateMeters = (time) => {
            if (!this.metersEnabled) return;
            if (time - lastUpdate >= this.meterIntervalMs) {
                this.updateMeters();
                lastUpdate = time;
            }
            this.animationId = requestAnimationFrame(updateMeters);
        };
        this.animationId = requestAnimationFrame(updateMeters);
    }

    updateMeters() {
        const now = Date.now();
        const peakHoldDuration = 1000; // ms to hold peak
        const peakFallRate = 0.05; // units per frame

        // Update channel meters
        this.channelConfig.forEach(config => {
            const meter = this.meters[config.id];
            if (!meter || !meter.bar) return;

            const db = this.mixer.getMeterLevel(config.id);
            // Convert dB to percentage (0 = -60dB, 100 = 0dB)
            const percent = Math.max(0, Math.min(100, (db + 60) / 60 * 100));

            meter.bar.style.height = `${percent}%`;

            // Peak hold logic
            if (db > meter.peakValue) {
                meter.peakValue = db;
                meter.peakHoldTime = now;
            } else if (now - meter.peakHoldTime > peakHoldDuration) {
                meter.peakValue -= peakFallRate * 60; // Fall in dB
            }

            const peakPercent = Math.max(0, Math.min(100, (meter.peakValue + 60) / 60 * 100));
            meter.peak.style.bottom = `${peakPercent}%`;

            // Color based on level
            if (percent > 90) {
                meter.bar.style.background = 'linear-gradient(to top, #4f4, #ff0, #f00)';
            } else if (percent > 70) {
                meter.bar.style.background = 'linear-gradient(to top, #4f4, #ff0)';
            } else {
                meter.bar.style.background = 'linear-gradient(to top, #2a2, #4f4)';
            }
        });

        // Update master meter (uses the master output meter from audioEngine if available)
        const masterMeter = this.meters.master;
        if (masterMeter && masterMeter.bar) {
            // Sum all channel levels for master approximation
            let maxDb = -Infinity;
            this.channelConfig.forEach(config => {
                const db = this.mixer.getMeterLevel(config.id);
                if (db > maxDb) maxDb = db;
            });

            const percent = Math.max(0, Math.min(100, (maxDb + 60) / 60 * 100));
            masterMeter.bar.style.height = `${percent}%`;

            if (maxDb > masterMeter.peakValue) {
                masterMeter.peakValue = maxDb;
                masterMeter.peakHoldTime = now;
            } else if (now - masterMeter.peakHoldTime > peakHoldDuration) {
                masterMeter.peakValue -= peakFallRate * 60;
            }

            const peakPercent = Math.max(0, Math.min(100, (masterMeter.peakValue + 60) / 60 * 100));
            masterMeter.peak.style.bottom = `${peakPercent}%`;

            if (percent > 90) {
                masterMeter.bar.style.background = 'linear-gradient(to top, #4f4, #ff0, #f00)';
            } else if (percent > 70) {
                masterMeter.bar.style.background = 'linear-gradient(to top, #4f4, #ff0)';
            } else {
                masterMeter.bar.style.background = 'linear-gradient(to top, #2a2, #4f4)';
            }
        }
    }

    stopMeterAnimation() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    destroy() {
        this.stopMeterAnimation();
        Object.values(this.sliders).forEach(slider => slider?.destroy());
        this.sliders = {};
        this.meters = {};
    }
}
